<template>
  <span
    class="icon-badge"
    :class="{ 'icon-badge--empty': !color, 'icon-badge--has-icon': !!icon }"
    :style="badgeStyle"
    :role="label ? 'img' : null"
    :aria-label="label || null"
    :aria-hidden="label ? null : 'true'"
  >
    <span
      v-if="icon"
      class="material-symbols-outlined icon-badge__glyph"
      :class="{ 'icon-badge__glyph--dark': isLightBackground }"
      :style="glyphStyle"
    >
      {{ icon }}
    </span>
  </span>
</template>

<script>
// Relative luminance above which a white glyph reads poorly. Biased above the
// pure-contrast crossover (~0.18) so mid-tone team colors keep a white glyph.
const LIGHT_LUMINANCE_THRESHOLD = 0.45;

export function parseHexColor(value) {
  const hex = String(value || '')
    .trim()
    .replace(/^#/, '');

  if (!/^([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) {
    return null;
  }

  const full =
    hex.length <= 4
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
    a: full.length === 8 ? parseInt(full.slice(6, 8), 16) / 255 : 1,
  };
}

export function isLightColor(value) {
  const rgb = parseHexColor(value);
  if (!rgb) return false;

  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const luminance =
    0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);

  // A mostly transparent badge shows the (light) surface behind it.
  return rgb.a < 0.5 || luminance > LIGHT_LUMINANCE_THRESHOLD;
}

export default {
  name: 'IconBadge',
  props: {
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    size: {
      type: Number,
      default: 20,
    },
    label: {
      type: String,
      default: '',
    },
  },
  computed: {
    isLightBackground() {
      return isLightColor(this.color);
    },
    badgeStyle() {
      const px = `${this.size}px`;
      return {
        width: px,
        height: px,
        minWidth: px,
        maxWidth: px,
        backgroundColor: this.color || null,
      };
    },
    glyphSize() {
      // Same parity as the badge so the inset is a whole pixel; a half-pixel
      // inset gets snapped to one side and the glyph looks off-center.
      return this.size - 2 * Math.round(this.size * 0.2);
    },
    glyphStyle() {
      const px = `${this.glyphSize}px`;
      return { fontSize: px, width: px, height: px };
    },
  },
};
</script>

<style lang="less">
.icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;

  &--empty {
    background-color: var(--v-appMain-base);
  }

  // Own compositing layer so the circle and glyph snap to the pixel grid
  // together when a row lands on a fractional offset (e.g. table rows).
  &--has-icon {
    will-change: transform;
  }

  // Outranks host rules like `.list-field__item.active span` that recolor spans.
  & &__glyph.material-symbols-outlined {
    display: block;
    flex-shrink: 0;
    overflow: hidden;
    color: #fff;
    line-height: 1;
    user-select: none;
  }

  & &__glyph--dark.material-symbols-outlined {
    color: rgba(0, 0, 0, 0.75);
  }
}
</style>
